/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonButton,
  IonIcon,
  IonCardContent,
  IonModal,
  useIonAlert,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { add, close } from "ionicons/icons";
import { useStorage } from "../../contexts/StorageContext";
import useAuth from "../../hooks/useAuth";
import { Preferences } from "@capacitor/preferences";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import DeviceScanModal from "../../components/home/DeviceScanModal";
import Header from "../../components/Header";
import "./Settings.css";
import { detachDevice, getUserDevices } from "../../services/device";

interface ConnectedDevice {
  id: string;
  name: string;
  serialNumber: string;
  dateAdded: string;
  pair_id: string;
}

// Utility Functions
const formatDevice = (device: any): ConnectedDevice => {
  const attachedDate = new Date(device.attached_at);
  return {
    id: device.device_id,
    name:
      device.device_type === "biwaze_cough_system"
        ? "BiWaze® Cough"
        : "BiWaze® Clear",
    serialNumber: device.device_id,
    dateAdded: attachedDate.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    pair_id: device.pair_id,
  };
};

const saveDevicesToStorage = async (
  storage: any,
  devices: ConnectedDevice[]
) => {
  await Preferences.set({ key: "myDevices", value: JSON.stringify(devices) });
  if (storage) {
    await ionicStorage(storage, "myDevices", devices);
  }
};

const MyDevicesPage: React.FC = () => {
  const [devices, setDevices] = useState<ConnectedDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [deviceToDisconnect, setDeviceToDisconnect] =
    useState<ConnectedDevice | null>(null);
  const [isDeviceScanModalOpen, setIsDeviceScanModalOpen] = useState(false);

  const router = useIonRouter();
  const { user } = useAuth();
  const { storage, isStorageReady } = useStorage();
  const [presentAlert] = useIonAlert();

  // Auto-open the link device modal when navigated here with ?linkDevice=true.
  // useIonViewWillEnter fires every time the view is entered (including cached instances),
  // so the modal opens reliably even when Ionic reuses an existing page instance.
  useIonViewWillEnter(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("linkDevice") === "true") {
      setIsDeviceScanModalOpen(true);
    }
  });

  // Fetch stored devices from local storage
  const fetchStoredDevices = async () => {
    if (!storage || !isStorageReady) return;
    const storedDevices = await getIonicStorage(storage, "myDevices");
    if (storedDevices) setDevices(storedDevices);
  };

  // Fetch devices from API
  const fetchDevices = async () => {
    try {
      const attachedDevices = await getUserDevices(user!.profile_id);
      setDevices(attachedDevices);
      await saveDevicesToStorage(storage, attachedDevices);
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStoredDevices();
    fetchDevices();
  }, [storage, isStorageReady]);

  const handleBack = () => router.push("/settings", "back");

  const handleDisconnectDevice = async () => {
    if (!deviceToDisconnect) return;
    try {
      const response: any = await detachDevice(
        user!.profile_id,
        deviceToDisconnect.pair_id
      );

      setDevices(response);
      await saveDevicesToStorage(storage, response);
      presentAlert({
        header: "Device unlinked",
        message: `Successfully unlinked ${deviceToDisconnect.name} with SN: ${deviceToDisconnect.serialNumber}`,
        buttons: ["OK"],
        cssClass: "text-black font-semibold",
      });
    } catch (error) {
      console.error("Error disconnecting device:", error);
    } finally {
      setIsDisconnectModalOpen(false);
      setDeviceToDisconnect(null);
    }
  };

  const handleOpenDisconnectModal = (device: ConnectedDevice) => {
    setDeviceToDisconnect(device);
    setIsDisconnectModalOpen(true);
  };

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="My Devices"
        showBackButton={true}
        onBackButtonClick={handleBack}
      />

      <IonCardContent
        className="ion-padding"
        style={{ backgroundColor: "#F7F5FA", height: "100vh" }}
      >
        <div className="flex flex-col h-full" style={{ paddingBottom: "14px" }}>
          <div className="settings-title !mt-0 !ms-4">LINKED DEVICES</div>

          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            {devices.length > 0 ? (
              devices.map((device, index) => (
                <div
                  key={device.id}
                  className={`p-4 relative ${
                    index !== devices.length - 1
                      ? "border-b border-gray-200"
                      : ""
                  }`}
                >
                  <h3 className="text-xl font-semibold mb-1">{device.name}</h3>
                  <p className="text-gray-500 mb-1">
                    SN: {device.serialNumber}
                  </p>
                  <p className="text-gray-500">
                    Linked date: {device.dateAdded}
                  </p>

                  <button
                    className="absolute top-0 right-4 bottom-0 text-blue-600 flex items-center justify-center h-full"
                    onClick={() => handleOpenDisconnectModal(device)}
                  >
                    <IonIcon icon={close} style={{ fontSize: "24px" }} />
                  </button>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-bold">
                There are no linked BiWaze<sup>®</sup> Systems
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-center py-4 text-blue-600 cursor-pointer"
            onClick={() => setIsDeviceScanModalOpen(true)}
          >
            <IonIcon
              icon={add}
              style={{ fontSize: "20px", marginRight: "8px" }}
            />
            <span
              className="text-blue-600 text-lg font-semibold"
              style={{ color: "#0778CF" }}
            >
              Link Device
            </span>
          </div>
        </div>
      </IonCardContent>

      {/* Disconnect Modal */}
      <IonModal
        isOpen={isDisconnectModalOpen}
        onDidDismiss={() => setIsDisconnectModalOpen(false)}
        className="disconnect-modal"
      >
        <div className="p-6 flex flex-col items-center">
          <div className="w-full flex justify-end">
            <button
              onClick={() => setIsDisconnectModalOpen(false)}
              className="text-gray-400"
            >
              <IonIcon icon={close} style={{ fontSize: "24px" }} />
            </button>
          </div>

          <h2 className="text-2xl font-bold mb-2 text-center">
            {deviceToDisconnect?.name}
          </h2>
          <div className="bg-gray-100 rounded-lg w-full p-4 text-center mb-6">
            <p className="text-lg font-medium">
              SN: {deviceToDisconnect?.serialNumber}
            </p>
            <p className="text-gray-500">
              Linked {deviceToDisconnect?.dateAdded}
            </p>
          </div>
          <h3 className="text-xl font-medium mb-8 text-center">
            Are you sure you want to unlink your device?
          </h3>

          <div className="w-full space-y-4">
            <IonButton
              expand="block"
              onClick={handleDisconnectDevice}
              style={{
                "--border-radius": "30px",
                "--background": "#FF0025",
                height: "52px",
              }}
            >
              <span style={{ color: "#FFFFFF" }}>Unlink</span>
            </IonButton>

            <IonButton
              expand="block"
              fill="outline"
              onClick={() => setIsDisconnectModalOpen(false)}
              style={{
                "--border-radius": "30px",
                "--color": "#0778CF",
                "--border-color": "#05ACD9",
                height: "52px",
              }}
            >
              Stay linked
            </IonButton>
          </div>
        </div>
      </IonModal>

      {isDeviceScanModalOpen && (
        <DeviceScanModal
          isOpen={isDeviceScanModalOpen}
          onClose={() => {
            setIsDeviceScanModalOpen(false);
            fetchDevices();
          }}
        />
      )}
    </IonPage>
  );
};

export default MyDevicesPage;
